/**
 * What a `gap_drafts` field means, for the developer reading the raw document.
 *
 * Deliberately partial. A description on every field would be noise that trains
 * the eye to skip the icon, so a field earns one only when its name does not
 * tell you what is in it (`transferMechanism`), when the value is a code you
 * would otherwise go and look up (`detector: "D4"`), or when it is easy to
 * confuse with the field beside it (`missability`, which is an enum on `source`
 * and a 0..1 number on `gap`). `_id`, `scopeName` and `metadata.createdAt` get
 * nothing.
 *
 * Hand-written rather than generated. The prose exists in two places already —
 * the JSDoc in `@momentum/types` and the `description` keys of the finder and
 * validator JSON schemas in `gap-analysis/GAP_RUN_*_WORKFLOW.js` — and both were
 * mined to seed this, the JSON schemas in particular, since that text is the
 * definition the agent was actually filling against. Extracting either at build
 * time would mean a generated file, a build step and a CI drift check to keep a
 * map that changes a few times a quarter honest. If a description here goes
 * stale, a tooltip is slightly wrong; that is a much cheaper failure.
 *
 * Keys are paths from the document root, with `[]` standing in for any array
 * index — every entry of `source.evidence` has the same meaning, so they share
 * one description.
 */
const FIELD_DESCRIPTIONS: Record<string, string> = {
  // ── The document's own shape ──────────────────────────────────────────
  variant:
    'Which pipeline produced this draft — sales or delivery. Always matches source.metadata.variant.',
  executionId:
    'The gap-analysis execution this gap came from. Joins to the gap_analysis_runs collection.',
  sourceGapId:
    'The gap’s id within that execution, e.g. "R1-KVTC-01". Ids restart at R1- on every execution, so this is unique only alongside executionId — which is exactly what the persist scripts key their upsert on.',
  runNumber:
    'The run, within the execution, that first found this gap. An execution re-runs the finder until it stops turning up anything new, so a high number means the gap was hard to see.',
  scopeExternalId:
    'external_id of the project or deal record in canonical_records — the id to query that record by.',
  source:
    'The run’s own gap, verbatim. FROZEN: written once at insert and never touched again, so the finder and validator’s own words stay auditable no matter what later agents conclude.',
  gap: 'The product-facing gap being assembled on top of source. Starts nearly empty and each enrichment agent fills in the fields it owns, so {} means no agent has reached this draft yet.',
  agents:
    'What each enrichment agent did, keyed by agent. Sparse: there is no NOT_RUN status, so a missing key is the only way "never ran" is expressed.',
  severityReasoning:
    'Why the enrichment scored gap.severity the way it did, in its own words. Sits at the root rather than in gap because a published Gap carries severity as a bare number — the argument behind it stops at the draft. Not editable: a reviewer who disagrees edits gap.severity, and the disagreement then reads as the two sitting side by side.',
  missabilityReasoning:
    'Why the enrichment scored gap.missability the way it did, in its own words. Sits at the root for the same reason severityReasoning does, and on the same terms: not editable, and a reviewer who disagrees edits gap.missability instead.',
  roiDerivation:
    'How the enrichment reached gap.roiImpact, or why it wrote no figure. Developer-facing and draft-only — it sits beside gap rather than in it, which is what keeps it off the customer’s page, and it is expected to carry clause numbers, externalIds and document paths where roiImpact.why carries a manager’s explanation.',
  'roiDerivation.outcome':
    'CALCULATED or SKIPPED. Written on every enrichment, including the ones that priced nothing: a recorded skip naming the missing input is evidence about the prompt, where an absent roiImpact alone looks like an agent that never tried.',
  'roiDerivation.formula':
    'The arithmetic as one line, ending on the published headline — e.g. "€1,998,800 × 5% = €99,940 → €100K". Present exactly when the outcome is CALCULATED.',
  'roiDerivation.inputs':
    'Every number the formula consumed, each with where it was read. On a SKIPPED derivation it holds whatever WAS found, so the record shows how far the agent got.',
  'roiDerivation.inputs[].source':
    'Where the number was read, precisely enough to re-read it without asking: a collection, subtype, field and externalId, or a document path and the clause inside it.',
  'roiDerivation.reasoning':
    'The chain in prose: the gap’s failure mode, the contractual consequence it triggers, and why that consequence is the one priced. On a SKIPPED derivation, which input could not be established.',
  'review.status':
    'Where the draft stands with a human reviewer. The queue’s Keep and Drop are these values — approved and rejected.',

  // ── Provenance ────────────────────────────────────────────────────────
  'source.metadata.id':
    'Permanent per-run id, R<run>-<SLUG>-<nn>. Never renumbered, even if the gap is later merged or re-scored.',
  'source.metadata.scope.slug':
    'The scope’s short key, used in gap ids, file paths and the ledger. This is what ties a gap to its per-scope bookkeeping in the run’s scopes[].',
  'source.metadata.detector':
    'Which finder lens surfaced the gap. Delivery uses D1–D8; sales uses C1–C8 (common sense), A1–A4 (analog) and H (hypothesis).',
  'source.metadata.foundBy':
    'Whether the gap came from reasoning about this deal (COMMON_SENSE), from a past deal that went the same way (ANALOG), or both.',
  'source.metadata.runNumber':
    'The R<run>- prefix of the gap id, stored as a number so it is queryable without a regex on the id.',
  'source.metadata.sourceCandidateIds':
    'The provisional finder candidates that merged into this one gap. More than one id means the merge agent judged them the same finding.',

  // ── The finder's claim ────────────────────────────────────────────────
  'source.claimType':
    'What kind of gap this is. The vocabulary differs per pipeline — delivery and sales do not share claim types.',
  'source.evidence[].externalId':
    'external_id of the source record backing this note, in canonical_records.',
  'source.evidence[].s3Path':
    'Path into the SharePoint S3 mirror, when the source is a document rather than a record.',
  'source.drift':
    'The drift point: the single moment the gap became flaggable. Not a timeline — just the one fact that made the gap knowable, and the date the flag could first have been raised.',
  'source.drift.date':
    'The date of the FACT itself, not of the record that carries it. A contract signed in March and filed in May dates to March.',
  'source.projectOverview':
    'The delivery or sales story up to the gap, ending on the background the gap turns on. Narrower than fullProjectContext, which covers the whole lifecycle.',
  'source.fullProjectContext':
    'The full project or deal background across every lifecycle stage reached — the long one.',

  // ── The finder's scores ───────────────────────────────────────────────
  'source.confidence':
    'How confident the finder is that the gap is real. Five steps, LOW through HIGH — this is about the claim being true, not about it mattering.',
  'source.impact':
    'What is at risk if the gap goes unaddressed — days of slip, € exposure.',
  'source.urgency':
    'How soon the gap becomes irreversible, measured against the next decision point rather than against the calendar.',
  'source.missability':
    'The probability a competent owner misses this without the analysis. EXTREME sits above HIGH: written down nowhere, and missed unless surfaced.',
  'source.customerFriction':
    'How much the recommended action burdens or disrupts the customer. A purely internal fix is LOW. Not a measure of the gap — a measure of the fix.',
  'source.precedents':
    'Past deals where this same mistake already played out, with what it cost and why this deal is exposed to the same mechanism.',
  'source.precedents[].documentedCost':
    'What the past mistake actually cost, in source currency, backed by the record or labelled as a proxy. Never invented.',
  'source.precedents[].transferMechanism':
    'Why the current deal is exposed to the same mechanism that sank the past one — the argument that this precedent applies here at all.',
  'source.precedentSearchNote':
    'The disclosed search behind an EMPTY precedents list: what was looked for and where, so "no precedent" can be told apart from "did not look".',
  'source.doNow':
    'A small immediate action, when there is one worth taking before the full fix.',
  'source.isHypothesis':
    'True marks an open hypothesis rather than an established gap — worth checking, not yet worth acting on.',
  'source.requiresBaseRate':
    'Sales only. Whether the validator is obliged to produce a base rate for this gap.',
  'source.validation':
    'The validator’s ruling. ABSENT means the gap was never adjudicated — its validator batch failed — which is different from any verdict value.',

  // ── The validator ─────────────────────────────────────────────────────
  'source.validation.verdict':
    'CONFIRMED, WEAKENED, CONTRADICTED or UNVERIFIABLE. Only CONTRADICTED means the gap was killed; UNVERIFIABLE means it could not be settled either way.',
  'source.validation.headline':
    'One line: the outcome with its qualifiers, carrying the counter-evidence when CONTRADICTED.',
  'source.validation.revisedImpact':
    'The finder’s impact, re-scored after the checks. Stays LOW/MEDIUM/HIGH even when the gap is CONTRADICTED.',
  'source.validation.revisedUrgency':
    'The finder’s urgency, re-scored after the checks. Stays LOW/MEDIUM/HIGH even when the gap is CONTRADICTED.',
  'source.validation.revisedMissability':
    'The finder’s missability, re-scored after the checks. NONE appears only on a CONTRADICTED gap.',
  'source.validation.revisedCustomerFriction':
    'The finder’s customerFriction, re-scored against the action that survived validation. NONE appears only on a CONTRADICTED gap.',
  'source.validation.baseRate':
    'How often this absence actually hurt, across comparable deals — e.g. "planned 6/14 · absent-and-fine 5/14 · absent-and-hurt 3/14". The denominator is the part that makes it mean anything: absent 8 times but harmful only 3 is an argument against the gap.',
  'source.validation.cheapestCheck':
    'The cheapest concrete check that would flip the verdict — one call, one document, one query. Mandatory when the verdict is UNVERIFIABLE.',
  'source.validation.precedentCheck':
    'Whether the finder’s precedent story held up under checking, or whether the past deal turned out not to be analogous after all.',
  'source.validation.evidenceAgainst':
    'Counter-evidence found while checking. Empty means nothing argues against the gap — not that nobody looked; searchMethodDisclosure says what was searched.',
  'source.validation.chronologyVerdict':
    'Whether the fact the gap rests on is still current. A gap resting on a superseded draft is not a gap — this is validity, not prioritisation.',
  'source.validation.searchMethodDisclosure':
    'The exact queries, fields and hit counts behind any claimed absence. This is what makes "there is no such document" checkable rather than asserted.',

  // ── The product layer ─────────────────────────────────────────────────
  'gap.externalRef':
    'Strong reference to the entity this gap was extracted from, in the same shape commitment signals use, so the two collections join on one external identity.',
  'gap.severity':
    'Business impact if unaddressed, as a number 0..1 where 1 is catastrophic. Recomputed by the enrichment from the record rather than mapped from source.impact, which is a LOW/MEDIUM/HIGH finder score on a different question. The reasoning behind the number is at severityReasoning.',
  'gap.missability':
    'How likely the project is to miss this gap on its own, as a number 0..1 where 1 is certain to be missed. Recomputed by the enrichment against the escalation timeline and the precedents rather than mapped from source.missability, which is the finder’s first-pass enum on the same question. The reasoning behind the number is at missabilityReasoning.',
  'gap.projectOverview':
    'The detail page’s lede: the account and project narratives, plus gapSummary — the one field that states what is missing.',
  'gap.roiImpact':
    'Estimated ROI of resolving the gap: a headline figure as authored, plus optionally why that number.',
  'gap.actionItems': 'Structured, ordered fix steps, each with its rationale.',
  'gap.escalation':
    'The dated evidence timeline of how the gap escalated. Refined from source.drift, which records only the single drift point.',
  'gap.seenBefore':
    'Past analog projects with a story each. The product-facing form of source.precedents.',
};

/**
 * The description for a document path, or `undefined` where the field was judged
 * self-explanatory.
 *
 * Numeric segments collapse to `[]`, so `source.evidence.0.s3Path` and
 * `source.evidence.7.s3Path` both resolve to the one entry above.
 */
export function describeField(path: string): string | undefined {
  const normalizedPath = path
    .split('.')
    .map((segment) => (/^\d+$/.test(segment) ? '[]' : segment))
    .join('.')
    .replace(/\.\[\]/g, '[]');

  return FIELD_DESCRIPTIONS[normalizedPath];
}
